import axios from '@axios'
import { apiDomain, authDomain } from '@src/app.config.json'
export const state = {
  currentUser: getSavedState('auth.currentUser'),
  userProfile: {},
}

export const mutations = {
  UPDATE_SUBSCRIBE(state, products) {
    let subscriptions = []
    products.subscription.forEach((element) => {
      subscriptions.push(element.slush)
    })
    state.currentUser.subscriptions = subscriptions
  },
  SET_CURRENT_USER(state, user) {
    state.currentUser = user
    saveState('auth.currentUser', user)
    setDefaultAuthHeaders(state)

    if (user) {
      state.userProfile.firstName = user.firstName
      state.userProfile.lastName = user.lastName
      state.userProfile.photo = user.photo
    }
  },
  SET_BASE_URL(state, user) {
    if (user.isAdmin) {
      setDefaultBaseURL(user.isAdmin)
    }
  },
  SET_CURRENT_USER_PROFILE_PICTURE(state, pictureUrl) {
    if (pictureUrl) {
      let clonedProfile = Object.assign({}, state.userProfile)
      clonedProfile.photo = pictureUrl.photo
      state.userProfile = clonedProfile
    }
  },
  SET_BALANCE(state, balance) {
    state.currentUser.balance = balance
    saveState('auth.currentUser', state.currentUser)
  },
  SET_PROFILE(state, profile) {
    for (let field in profile) {
      if (
        [
          'access_token',
          'refresh_token',
          'is_admin',
          'is_enabled',
          'uid',
        ].includes(field)
      )
        continue
      if (state.currentUser.hasOwnProperty(field)) {
        if (state.currentUser.id === profile.id) {
          state.currentUser[field] = profile[field]
        }
      }
    }

    if (profile) {
      state.userProfile.firstName = profile.firstName
      state.userProfile.lastName = profile.lastName
      state.userProfile.photo = profile.photo
    }

    saveState('auth.currentUser', state.currentUser)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser
  },
  isAdmin(state) {
    if (state.currentUser) {
      return state.currentUser.isAdmin === true
    }
    return false
  },
  isSubAccount(state) {
    if (state.currentUser) {
      return !!state.currentUser.parent_id === true
    }
    return false
  },
  currentUser: (state) => state.currentUser,
  userProfile: (state) => state.userProfile,
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    dispatch('validate')
  },

  // Logs in the current user.
  logIn({ commit }, { username, password } = {}) {
    // Check if redirectValue is equal to 'support_ticket'
    const redirectValue = getRedirectFromValue();
    return axios
      .post(
        `${authDomain}/auth/signin`,
        { email: username, password: password },
        { params: { timestamp: Date.now(), Redirect: redirectValue } }
      )
      .then((response) => {
        if (response.status === 200) {

          let user = response.data;

          commit('SET_CURRENT_USER', user);
          commit('SET_BASE_URL', user);
          if (redirectValue === 'support_ticket') {
            redirectToExternal('https://support.plasgate.com/auth', user.refreshToken); // Redirect user
            return;
          }
          return user;
        }
        return response;
      });
  },



  // Logs out the current user.
  logOut({ commit }) {
    commit('SET_CURRENT_USER', null)
    delete axios.defaults.headers.common['Authorization']
  },

  // register the user
  register({ commit, dispatch, getters }, new_user = {}) {
    if (getters.loggedIn) {
      return dispatch('validate')
    }
    return axios
      .post(`${authDomain}/auth/signup`, new_user)
      .then((response) => {
        const user = response.data
        return user
      })
  },

  activateOTP({ commit, dispatch, getters }, OTP) {
    if (getters.loggedIn) return dispatch('validate')
    return axios.post(`${authDomain}/auth/activate`, {
      token: OTP,
    })
  },
  // register the user
  resetPassword({ commit, dispatch, getters }, { email } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    const formData = new FormData()
    formData.append('email', email)
    return axios.post('/account-recovery', formData).then((response) => {
      const message = response.data
      return message
    })
  },
  // forget passwor
  forgetPassword({ cmmit, dispatch, getters }, data = {}) {
    if (getters.loggedIn) return dispatch('validate')
    return axios.post(`${authDomain}/auth/reset-password`, data)
  },
  // verify token and update new password
  UpdateNewPassword({ commit, dispatch, getters }, data = {}) {
    if (getters.loggedIn) return dispatch('validate')
    return axios.patch(`${authDomain}/auth/update-password`, data)
  },

  newPassword({ commit, dispatch, getters }, { data } = {}) {
    if (getters.loggedIn) return dispatch('validate')
    const formData = new FormData()
    formData.append('password', data.password)
    return axios.post(`/forget-password/${data.token}`, formData)
  },
  // register the user
  changePassword({ commit, dispatch, getters }, { email, password } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    const formData = new FormData()
    formData.append('email', email)
    formData.append('password', password)
    return axios.post('/reset-password', formData).then((response) => {
      const message = response.data
      return message
    })
  },

  requestNewPassword({ commit }, newPassword = {}) {
    return axios.post('/auth/change-password', newPassword)
  },

  resentToken({ commit }, data = {}) {
    return axios.post(`${authDomain}/auth/resend-token`, data)
  },

  // register the user
  recovery({ commit, dispatch, getters }, { token } = {}) {
    const formData = new FormData()
    formData.append('token', token)
    return axios.post('/recovery/' + token, formData).then((response) => {
      const message = response.data
      return message
    })
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state, getters }, routeTo) {
    if (routeTo !== undefined) {
      // reset based URL
      if (routeTo.name === 'login' || routeTo.name === 'logout')
        setDefaultBaseURL(false)
    } else {
      setDefaultBaseURL(getters.isAdmin)
    }

    if (!state.currentUser) return Promise.resolve(null)

    // set refresh token header
    return axios
      .post(`${authDomain}/auth/token/refresh`)
      .then((response) => {
        commit('SET_CURRENT_USER', response.data)
        return response
      })
      .catch(() => {
        commit('SET_CURRENT_USER', null)
        return null
      })
  },

  verifyUser({ commit, state }, account) {
    // Reset login url
    axios.defaults.baseURL = apiDomain

    return axios.post('/login', account).then((resp) => {
      if (resp.status === 200) {
        setDefaultBaseURL(state.currentUser.is_admin)
        commit('SET_CURRENT_USER', resp.data)
      }
      return resp.data
    })
  },

  async confirmToken({ commit }, user) {
    return axios.post(`/confirm`, user)
  },

  async verifyRecaptcha({ commit }, token) {
    return axios.get(`${authDomain}/auth/verify-recaptcha`, {
      params: { response: token },
    })
  },
  async verifyPassword({ commit, dispatch }, password = '') {
    return axios.post(`${authDomain}/auth/verify-password`, {
      password: password,
    })
  },
  async disableMfa({ commit, dispatch }, method = '') {
    const currentMethod = method.toLocaleLowerCase()
    return axios.post(`${authDomain}/auth/mfa-disable`, {
      method: currentMethod,
    })
  },
  async enableMfa({ commit, dispatch }, method = '') {
    const currentMethod = method.toLocaleLowerCase()
    return axios.post(`${authDomain}/auth/mfa-enable`, {
      method: currentMethod,
    })
  },
  async verifyMfa({ commit, dispatch }, token = '') {
    return axios.post(`${authDomain}/auth/mfa-verify`, {
      token: token,
    })
  },
  async verifyLoginMfa({ commit, dispatch }, token = '') {
    return axios.post(`${authDomain}/auth/mfa-login-verify`, {
      token: token,
    })
  },
  async resendMfaOtp({ commit, dispatch }) {
    return axios.post(`${authDomain}/auth/mfa-resend`)
  },
}

// ===
// Private helpers
// ===

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function getRedirectFromValue() {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectFrom = urlParams.get('redirectFrom') || null;
  return redirectFrom;
}

function redirectToExternal(url, token) {
  const fullUrl = `${url}?token=${encodeURIComponent(token)}`;
  window.location.href = fullUrl;
}

function setDefaultAuthHeaders(state, refresh = false) {
  if (state.currentUser) {
    axios.defaults.headers.common.Authorization = `Bearer ${state.currentUser.accessToken
      }`
  } else {
    delete axios.defaults.headers.common['Authorization']
  }
}

function setDefaultBaseURL(isAdmin) {
  if (isAdmin) {
    axios.defaults.baseURL = apiDomain
  } else {
    axios.defaults.baseURL = apiDomain
  }
}
